import React, { useState, ReactNode, MouseEvent, useEffect, Fragment, useMemo, useRef } from "react";
import { twMerge } from "tailwind-merge";
import useHover from "./react-use-hover";

interface TooltipProps {
  text: string;
  position: "top" | "bottom" | "left" | "right";
  children: ReactNode;
  delay?: boolean;
}

const calculatePosition = (position = "right") => {
  switch (position) {
    case "top":
      return {
        bottom: "100%",
        left: 0,
        right: 0,
      };
    case "bottom":
      return {
        top: "100%",
        left: 0,
        right: 0,
      };
    case "left":
      return {
        top: 0,
        right: "100%",
        bottom: 0,
      };
    case "right":
      return {
        top: 0,
        left: "100%",
        bottom: 0,
      };
    default:
      return { top: 0, left: "100%", bottom: 0 };
  }
};

const Tooltip: React.FC<TooltipProps> = ({ text, position, delay = false, children }) => {
  const [tooltipPosition, setTooltipPosition] = useState<{
    top?: number | string;
    right?: number | string;
    left?: number | string;
    bottom?: number | string;
  }>({ top: 0, right: 0 });
  const [isTooltipVisible, setTooltipVisible] = useState(true);

  const showTooltip = (e: MouseEvent<HTMLDivElement>) => {
    if (!isTooltipVisible) {
      const tooltipPosition = calculatePosition(position);

      // Устанавливаем позицию тултипа
      setTooltipPosition(tooltipPosition);
      setTooltipVisible(true);
    }
  };
  const hideTooltip = () => {
    setTooltipVisible(false);
  };

  return (
    <div className="relative" onMouseOver={showTooltip} onMouseOut={hideTooltip}>
      {children}
      {isTooltipVisible && (
        <div className="absolute text-sm flex flex-row items-center pl-2.5" style={tooltipPosition}>
          <div className="triangle-left">
            <div className="flex px-3 py-1 bg-white border-r-4 rounded-md shadow-custom">{text}</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Tooltip;

export type PositionType =
  | "right"
  | "top"
  | "left"
  | "bottom"
  | "top-right"
  | "top-left"
  | "bottom-right"
  | "bottom-left"
  | "filter"
  | "fiveWhy";

export const TooltipItem = ({
  children,
  tooltipsText,
  tooltipsComponent,
  tooltipsReactNode,
  position,
  disable = false,
  className,
  cursor = true,
  textClassName,
  wrapperClassName,
  hoverClassName,
  delay = 0,
  // Touch devices have no hover: without tap-to-open, help icons stay invisible on mobile.
  openOnMobileTap = true,
}: {
  children: ReactNode;
  tooltipsText: string;
  tooltipsComponent?: any;
  tooltipsReactNode?: ReactNode;
  position: PositionType;
  disable?: boolean;
  className?: string;
  textClassName?: string;
  wrapperClassName?: string;
  hoverClassName?: string;
  cursor?: boolean;
  delay?: number;
  openOnMobileTap?: boolean;
}) => {
  const isTop = position.includes("top");
  const isRight = position.includes("right");
  const isBottom = position.includes("bottom");
  const isLeft = position.includes("left");
  const filterPosition = position.includes("filter");
  const noticeFiveWhy = position.includes("fiveWhy");

  const [isHover, hoverProps] = useHover({ mouseEnterDelayMS: delay, mouseLeaveDelayMS: delay });

  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const [mobileStyle, setMobileStyle] = useState<React.CSSProperties | undefined>(undefined);
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  const isMobile = useMemo(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia?.("(max-width: 640px)")?.matches ?? window.innerWidth <= 640;
  }, []);

  useEffect(() => {
    if (!isMobile) return;
    if (!isMobileOpen) {
      setMobileStyle(undefined);
      return;
    }

    const apply = () => {
      const wrapperEl = wrapperRef.current;
      const tooltipEl = tooltipRef.current;
      if (!wrapperEl || !tooltipEl) return;

      const rect = wrapperEl.getBoundingClientRect();
      const viewportH = window.innerHeight;

      // Match app container padding (p-10 = 2.5rem). Keep tooltip inside viewport.
      const sidePaddingPx = 40; // 2.5rem * 16px
      const horizontalInset = sidePaddingPx;
      const maxWidth = window.innerWidth - horizontalInset * 2;

      // Default: show below icon.
      const gap = 12;
      let top = rect.bottom + gap;

      // Ensure tooltip has the target width before measuring height.
      tooltipEl.style.width = `${maxWidth}px`;

      const height = tooltipEl.getBoundingClientRect().height;
      const overflowBottom = top + height > viewportH - gap;

      // If doesn't fit below, show above.
      if (overflowBottom) {
        top = Math.max(gap, rect.top - gap - height);
      }

      setMobileStyle({
        position: "fixed",
        left: `${horizontalInset}px`,
        top: `${top}px`,
        width: `${maxWidth}px`,
        maxWidth: `${maxWidth}px`,
        height: "fit-content",
        zIndex: 1000,
        // allow scrolling if tooltip content is very tall
        maxHeight: `${viewportH - gap * 2}px`,
        overflow: "auto",
      });
    };

    // Run twice: first paint, then after layout to get correct height.
    requestAnimationFrame(() => {
      apply();
      requestAnimationFrame(apply);
    });

    const onResize = () => apply();
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onResize, true);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onResize, true);
    };
  }, [isMobileOpen, isMobile]);

  useEffect(() => {
    if (!isMobile) return;
    if (!isMobileOpen) return;

    const onPointerDown = (e: PointerEvent) => {
      const wrapperEl = wrapperRef.current;
      const tooltipEl = tooltipRef.current;
      const target = e.target as Node | null;
      if (!target) return;

      // Click inside wrapper/tooltip should keep it open.
      if (wrapperEl?.contains(target) || tooltipEl?.contains(target)) return;
      setIsMobileOpen(false);
    };

    document.addEventListener("pointerdown", onPointerDown, true);
    return () => document.removeEventListener("pointerdown", onPointerDown, true);
  }, [isMobile, isMobileOpen]);

  useEffect(() => {
    return () => {
      if (delay > 0 && hoverProps) {
        setTimeout(() => {
          hoverProps.onMouseLeave();
        }, delay);
      }
    };
  }, [hoverProps, delay]);

  return (
    <div
      className={twMerge(
        cursor ? "cursor-pointer" : "",
        "relative inline-block group",
        wrapperClassName,
        isHover ? hoverClassName : ""
      )}
      ref={wrapperRef}
      {...(hoverProps || {})}
      onPointerDownCapture={(e) => {
        if (!isMobile) return;
        if (!openOnMobileTap) return;
        if (disable) return;
        // Toggle tooltip on mobile tap. Capture phase so child components that stopPropagation
        // (e.g. icons inside clickable rows) won't block this.
        const target = e.target as Node | null;
        if (isMobileOpen && target && tooltipRef.current?.contains(target)) return;
        setIsMobileOpen((v) => !v);
      }}
    >
      {children}
      {!disable && (
        <div
          ref={tooltipRef}
          style={mobileStyle}
          className={twMerge(
            isMobile
              ? `hidden tablet:flex rounded-md shadow-custom bg-white px-3.5 py-1 text-[14px] text-wrap tg-tooltip-popover`
              : `hidden tablet:group-hover:flex transform ${(isRight && isTop && `absolute bottom-full -left-2.5 z-20 mb-3`) ||
                (isRight && isBottom && `absolute top-full -left-2.5 z-20 mt-3`) ||
                (isLeft && isTop && `absolute bottom-full -right-2.5 z-20 mb-3`) ||
                (isLeft && isBottom && `absolute top-full -right-2.5 z-20 mt-3`) ||
                (isRight && `absolute left-full top-1/2 z-20 ml-3 -translate-y-1/2`) ||
                (isTop && `absolute bottom-full left-1/2 z-20 mb-3 -translate-x-1/2`) ||
                (isLeft && `absolute right-full top-1/2 z-20 mr-3 -translate-y-1/2`) ||
                (filterPosition && `absolute w-[310px] top-full -right-2.5 z-20 mt-3`) ||
                (isBottom && `absolute left-1/2 top-full z-20 mt-3 -translate-x-1/2`)
                } rounded-md shadow-custom bg-white px-3.5 py-1 text-[14px] opacity-0 group-hover:opacity-100 text-wrap tg-tooltip-popover`,
            className,
            isMobile && !isMobileOpen ? "hidden" : ""
          )}
          onPointerDown={(e) => {
            // Prevent tooltip from toggling/closing due to parent handlers.
            e.stopPropagation();
          }}
        >
          <span
            className={`absolute -z-10 h-2 w-2 ${(isRight &&
              isTop &&
              `left-3 -bottom-[5px] rotate-45 rounded-r-sm border-b border-r border-light bg-white`) ||
              (isRight &&
                isBottom &&
                `left-3 -top-[5px] rotate-45 rounded-r-sm border-t border-l border-light bg-white`) ||
              (isLeft &&
                isTop &&
                `right-3 -bottom-[5px] rotate-45 rounded-r-sm border-r border-b border-light bg-white`) ||
              (isLeft &&
                isBottom &&
                `right-3 -top-[5px] rotate-45 rounded-r-sm border-l border-t border-light bg-white`) ||
              (isRight &&
                `left-[-5px] top-1/2 -translate-y-1/2 rotate-45 rounded-r-sm border-b border-l border-light bg-white`) ||
              (isTop &&
                `bottom-[-5px] left-1/2 -translate-x-1/2 rotate-45 rounded-l-sm border-b border-r border-light bg-white`) ||
              (isLeft &&
                `right-[-5px] top-1/2 -translate-y-1/2 rotate-45 rounded-sm border-r border-t border-light bg-white`) ||
              (isBottom &&
                `top-[-5px] left-1/2 -translate-x-1/2 rotate-45 rounded-sm border-l border-t border-light bg-white`)
              }`}
          ></span>
          {tooltipsComponent ? (
            <div className="flex flex-col" dangerouslySetInnerHTML={{ __html: tooltipsComponent }} />
          ) : tooltipsReactNode ? (
            <>{tooltipsReactNode}</>
          ) : (
            <span className={twMerge(textClassName ? textClassName : "whitespace-nowrap")}>{tooltipsText}</span>
          )}
        </div>
      )}
    </div>
  );
};
